import React, { useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { svg } from "../../../Assets/svg";
import { useAppDispatch } from "../../../store";
import UploadedSampleFileDisplay from "../../Components/UploadedSampleFileDisplay";
import Dragger from "antd/es/upload/Dragger";
import { Button, message, Upload } from "antd";
import { exportPriceSheetAPI } from "../PriceSheet/priceSheetApis";
import { exportProductVariantsAPI, importVariantsAPI } from "./productApis";
import { PRODUCT_FILTER_KEY, PRODUCT_REFETCH_KEY, PRODUCT_SCROLL_KEY } from "./productsTypes";

const ImportVariants = () => {
  const navigate = useNavigate();

  const { productId } = useParams();
  const dispatch = useAppDispatch();

  // ✅ State Management
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState<boolean>(false);
  const messageCloserRef = useRef<any>(null);

  const importInstructions = [
    {
      label: "Please do not change any grey cell value.",
      fullBold: true,
    },
    {
      label: "Product Code",
      required: true,
      instruction: "Accepts all input formats and must be unique.",
    },
    {
      label: "Variant Title",
      required: true,
      instruction: "Accepts all input formats and must be unique.",
    },
    {
      label: "Category",
      required: false,
      instruction: (
        <>
          Only add category from{" "}
          <span className="fw-bold">
            Adjustable Pallet Racking (APR), Shuttle Racking, Drive In, Drive
            Through
          </span>
          . We will not accept any other values.
        </>
      ),
    },
    {
      label: "Machine",
      required: false,
      instruction:
        "Accepts all input formats. If that value not exiting in machines list then it will create new machine automatically.",
    },
  ];

  const handleRemoveFile = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (loading) return;
    setFile(null);
  };

  const draggerProps = {
    name: "file",
    multiple: false,
    maxCount: 1,
    accept: ".xlsx",
    beforeUpload: (file: File) => {
      // CHANGED: Added strict extension check
      const isXlsx = file.name.toLowerCase().endsWith(".xlsx");
      if (!isXlsx) {
        message.error("You can only upload .xlsx files!");
        return Upload.LIST_IGNORE;
      }
      setFile(file);
      return false;
    },
    showUploadList: false,
    style: {
      border: "2px dashed #d9d9d9",
      backgroundColor: "#fafafa",
      padding: "20px 0",
      marginBottom: "30px",
    },
  };

  const handleExport = () => {
    setExporting(true);
    const closeMessage = message.loading("Download in progress...", 0);
    messageCloserRef.current = closeMessage;
    dispatch(
      exportProductVariantsAPI({
        id: productId as string,
        onSuccess: () => {
          setExporting(false);
          closeMessage();
          messageCloserRef.current = null;
        },
        onError: () => {
          setExporting(false);
          closeMessage();
          messageCloserRef.current = null;
        },
      }),
    );
  };

  const handleSubmit = () => {
    if (!file) {
      message.error("Please upload a file first.");
      return;
    }

    setLoading(true);
    dispatch(
      importVariantsAPI({
        productId: productId as string,
        file: file,
        onSuccess: () => {
          sessionStorage.removeItem(PRODUCT_FILTER_KEY);
          sessionStorage.removeItem(PRODUCT_SCROLL_KEY);
          sessionStorage.setItem(PRODUCT_REFETCH_KEY, "1");
          setLoading(false);
          setFile(null);
          message.success(
            "Variants import is in-progress, this may take some time to complete.We will notify you once process completed",
          );
          navigate(-1); // Navigate back to list
        },
        onError: () => {
          setLoading(false);
        },
      }),
    );
  };

  return (
    <div className="page-content px-40 py-40">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="d-flex align-items-center">
          <div
            className="text-primary fs-6 fw-medium d-inline-flex align-items-center backArrowLink mb-0 cursor-pointer"
            onClick={() => navigate(-1)}
            title="Back"
          >
            <span className="lh-normal">{svg.backArrow}</span>
          </div>
          <h2 className="text-black fs-2 fw-bolder page-title mb-0 ms-2">
            Import Variants
          </h2>
        </div>
        <div className="d-md-inline-block d-none">
          <button
            onClick={() => {
              navigate(-1);
            }}
            className="btn btn-outline-secondary btn-lg min-w-126 cancelButton me-lg-4 me-2"
            disabled={loading}
          >
            CANCEL
          </button>
          <button
            onClick={handleSubmit}
            className="btn btn-primary btn-lg min-w-126"
            disabled={loading || !file}
          >
            {loading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                PROCESSING...
              </>
            ) : (
              "SUBMIT"
            )}
          </button>
        </div>
      </div>
      <div className="card page-card border-0 shadow rounded-4 p-4 p-md-5">
        {/* Conditional File Upload Area */}
        <div className="upload-container">
          {file ? (
            <UploadedSampleFileDisplay
              file={file}
              loading={loading}
              onRemove={handleRemoveFile}
            />
          ) : (
            <Dragger {...draggerProps}>
              <p className="ant-upload-drag-icon text-center">
                {svg?.importIcon}
              </p>
              <p className="ant-upload-text fw-semibold text-center">
                Drag & Drop XLSX File /{" "}
                <span className="text-primary text-decoration-underline">
                  CLICK HERE
                </span>
              </p>
            </Dragger>
          )}
        </div>

        <div className="text-center mt-3 mb-4">
          <Button
            type="link"
            onClick={() => {
              if (exporting) return;
              handleExport();
            }}
            className="text-decoration-underline fw-semibold text-primary fs-6"
          >
            Export XLSX File
          </Button>
        </div>

        {/* Mobile Buttons */}
        <div className="d-md-none text-center mb-4">
          <button
            onClick={() => navigate(-1)}
            className="btn btn-outline-secondary btn-lg min-w-126 cancelButton me-lg-4 me-2"
            disabled={loading}
          >
            CANCEL
          </button>
          <button
            onClick={handleSubmit}
            className="btn btn-primary btn-lg min-w-126"
            disabled={loading || !file}
          >
            {loading ? "IMPORTING..." : "SUBMIT"}
          </button>
        </div>

        {/* Import Instructions */}
        <div className="import-instructions">
          <h3 className="fs-5 fw-bold mb-3">Import XLS or XLSX Instruction</h3>

          <ul className="instruction-list ps-3">
            {importInstructions.map((item: any, index) => (
              <li key={index} className="mb-2 fs-6 text-black">
                {item.fullBold ? (
                  <span className="fw-bold">{item.label}</span>
                ) : (
                  <>
                    <span className="fw-bold">
                      {item.label}
                      {item.required !== undefined &&
                        (item.required ? " (required)" : " (optional)")}
                    </span>
                    <span> : {item.instruction}</span>
                  </>
                )}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ImportVariants;
